import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SelectItem, SelectItemProps, SelectProps } from "./type";
import { CSSTransition } from "react-transition-group";
import "../../styles/animation.css";
import "../../styles/select.scss";

const SelectItemComponent = ({
  isSelected,
  item,
  onChange,
}: SelectItemProps) => {
  const onClick = () => onChange(!isSelected);
  return (
    <div className="normal-button select-item" onClick={onClick}>
      <div className="item">
        {isSelected && <div className="select__item-selected" />}
      </div>
      <span style={{ whiteSpace: "nowrap" }} key={item.value}>
        {item.label}
      </span>
    </div>
  );
};

const Select = (
  {
    style,
    label,
    data = [],
    values,
    onChanged,
    isMultiple,
    editable,
    customContent,
    readOnly,
    onItemChanged,
    invalid,
  }: SelectProps,
  ref: any
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Array<any> | undefined>(
    values
  );
  const [items, setItems] = useState(data);
  const [inputVal, setInputVal] = useState(values?.[0]?.label || "");
  const outlineClass = isOpen ? "select-active" : "select-inactive";
  useImperativeHandle(ref, () => {
    return {
      toggle: () => {
        setIsOpen(!isOpen);
      },
    };
  });
  useEffect(() => {
    if (values) {
      setSelectedItems(values);
      setInputVal(values?.[0]?.label || "");
    }
  }, [values]);
  useEffect(() => {
    setItems(data);
  }, [data]);
  const getIsSelected = (item: SelectItem) => {
    if (isMultiple)
      return selectedItems?.find((si) => si.value == item.value) != null;
    return selectedItems?.[0]?.value == item.value;
  };
  const getLabel = () => {
    if (!selectedItems) return label;
    if (isMultiple)
      return selectedItems.length > 0
        ? `${label} (${selectedItems.length})`
        : label;
    return selectedItems.length > 0 ? selectedItems[0].label : label;
  };
  const onChange = (item: SelectItem) => (isSelected: boolean) => {
    onItemChanged?.(item, isSelected);
    let newSelectedItems: Array<any> | undefined = [];
    if (isMultiple) {
      newSelectedItems = isSelected
        ? [...(selectedItems || []), item]
        : selectedItems?.filter((si) => si.value != item.value);
    } else {
      setIsOpen(false);
      newSelectedItems = isSelected ? [item] : [];
      setInputVal(newSelectedItems?.[0]?.label || "");
    }
    onChanged?.(newSelectedItems || []);
    setSelectedItems(newSelectedItems);
  };
  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setInputVal(val);
    setIsOpen(true);
    if (val == "") {
      setItems(data);
    } else {
      const res = data.filter((dt) =>
        dt.label.toLowerCase().includes(val.toLowerCase())
      );
      setItems(res);
    }
  };

  return (
    <div>
      {(customContent && customContent) || (
        <div
          className={`select-container ${
            !readOnly && "normal-button"
          } ${outlineClass} ${invalid ? "select-invalid" : ""}`}
          style={style}
          onClick={() => {
            !readOnly && setIsOpen(!isOpen);
          }}
        >
          {(!readOnly && editable && !isMultiple && (
            <input
              onChange={onChangeText}
              placeholder={label}
              value={inputVal}
              readOnly={readOnly}
              style={{ width: "100%" }}
            />
          )) || <span className="label">{getLabel()}</span>}
          {!readOnly && (
            <i className="material-icons" style={styles.icon}>
              {isOpen ? "expand_less" : "expand_more"}
            </i>
          )}
        </div>
      )}
      {isOpen && (
        <div
          onClick={() => {
            setIsOpen(false);
          }}
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 1,
          }}
        />
      )}
      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames="select"
        unmountOnExit
        onEnter={() => {}}
        onExited={() => {}}
      >
        <div className="selection-container selection-color">
          {(items.length > 0 &&
            items.map((item, index) => {
              return (
                <SelectItemComponent
                  key={`${item.value}-${index}`}
                  item={item}
                  isSelected={getIsSelected(item)}
                  onChange={onChange(item)}
                />
              );
            })) || <span className="empty">No options</span>}
        </div>
      </CSSTransition>
    </div>
  );
};

const styles: { [name: string]: React.CSSProperties } = {
  icon: {
    fontSize: 18,
  },
};

export default forwardRef(Select);
